import { CloseRounded } from '@mui/icons-material'
import { Box, Button, DialogActions, DialogContent, DialogTitle, FormControl, Typography } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { deleteResource, toggleFormEditResources } from '../../../../../redux/actions/resources'
import { IRootState } from '../../../../../redux/types'
import { StyledTabs } from '../../../../shared/components/StyledTabs'
import TabPanel from '../../../../shared/components/TabPanel'
import {
  BrandAutocomplete,
  CancelButton,
  CustomTextField,
  DeleteButton,
  Dropzone,
  RemoveContent,
  LanguageMultiAutocomplete,
  LineAutocomplete,
  ResourcecategoryAutocomplete,
  ResourceTypeMultiAutocomplete,
  RoleAutocomplete,
  SaveButton,
  VisibilityAutocomplete
} from '../../../../shared/components/inputs'
import InputCountry from '../../../../shared/components/inputs/InputCountry'
import useTabState from '../../../../shared/hooks/useTabState'
import { StyledDialog } from '../../../../shared/styles/StyledDialog'
import { CustomTab } from '../../../users/ui/components/modals/create/CustomTab'
import useEditHandleSubmit from '../hooks/useEditHandleSubmit'
import useEditMediaState from '../hooks/useEditMediaState'
import useInitialState from '../hooks/useInitialState'
import ResourceFileType from './ResourceFileType'

function ResourceModal() {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()

  const { resourceSelected, showEditForm } = useSelector((state: IRootState) => state.resource)
  const { isValidForm, errorTab, msgForm } = useSelector((state: IRootState) => state.validator)

  useInitialState()
  const { resource, handleInputChange, VISIBILITY } = useEditMediaState()
  const { handleSubmit } = useEditHandleSubmit(resource)
  const { currentTab, setCurrentTab, a11yProps } = useTabState()

  const handleRemoveCover = () => {
    handleInputChange('cover', null)
  }

  const handleRemoveResource = () =>{
    handleInputChange('fileName', null)
  }

  return (
    <StyledDialog
      open={showEditForm}
      onClose={() => dispatch(toggleFormEditResources())}
      aria-labelledby="dialog-resource-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="dialog-resource-title">
        <Grid container justifyContent="space-between">
          <Typography variant="h6">{t('edit')} media</Typography>
          <Button onClick={() => dispatch(toggleFormEditResources())} color="primary" variant="text">
            <CloseRounded />
          </Button>
        </Grid>
      </DialogTitle>

      <DialogContent sx={{ pb: 3, pt: 2 }}>
        {resourceSelected !== null && (
          <form noValidate autoComplete="off">
            <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Grid xs={12} md={resource.brand !== null ? 4 : 6}>
                <FormControl fullWidth variant="filled">
                  <BrandAutocomplete value={resource.brand} handleInputChange={handleInputChange} props={{ required: true }} />
                </FormControl>
              </Grid>

              {resource.brand !== null && (
                <Grid xs={12} md={4}>
                  <FormControl fullWidth variant="filled">
                    <LineAutocomplete value={resource.line} brand={resource.brand} handleInputChange={handleInputChange} />
                  </FormControl>
                </Grid>
              )}

              <Grid xs={12} md={resource.brand !== null ? 4 : 6}>
                <FormControl fullWidth variant="filled">
                  <ResourceTypeMultiAutocomplete value={resource.resourcetypes} handleInputChange={handleInputChange} />
                </FormControl>
              </Grid>
            </Grid>

            <Grid xs={12}>
              <StyledTabs
                id="tabs-edit-resources"
                value={currentTab}
                onChange={(_, newValue) => setCurrentTab(newValue)}
                aria-label="disabled tabs example"
              >
                <CustomTab error={errorTab.general} label={t('general')} {...a11yProps(0)} />
                <CustomTab error={errorTab.general} label={t('document')} {...a11yProps(1)} />
                <CustomTab error={errorTab.general} label={t('cover-image')} {...a11yProps(2)} />
              </StyledTabs>
            </Grid>

            <TabPanel value={currentTab} index={0}>
              <Grid container spacing={2}>
                <Grid xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="filled">
                    <RoleAutocomplete value={resource.role} handleInputChange={handleInputChange} />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <InputCountry value={resource.countries} handleInputChange={handleInputChange} />
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="filled">
                    <LanguageMultiAutocomplete value={resource.languages} handleInputChange={handleInputChange} />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <VisibilityAutocomplete value={resource.visibility} handleInputChange={handleInputChange} options={VISIBILITY} />
                </Grid>
                <Grid xs={12} md={4}>
                  <FormControl fullWidth variant="filled">
                    <CustomTextField inputName={'alias'} value={resource.alias} handleInputChange={handleInputChange} />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="filled">
                    <ResourcecategoryAutocomplete
                      value={resource.categories}
                      resourcetypes={resource.resourcetypes}
                      handleInputChange={handleInputChange}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={12}>
                  <FormControl fullWidth variant="filled">
                    <CustomTextField inputName={'description'} value={resource.description} handleInputChange={handleInputChange} />
                  </FormControl>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={currentTab} index={1}>
              <Grid container spacing={2}>
                <Grid xs={12}>
                  <Box
                    p={1}
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    justifyContent="center"
                    my={2}
                    borderRadius={8}
                    gap={8}
                    style={{ backgroundColor: '#FAFAFA' }}
                  >
                    {(resource.fileName) ? (
                      <RemoveContent
                        files={[`${import.meta.env.VITE_AWS_URL}/resources/${resource.fileName}`]}
                        onHandleClick={handleRemoveResource}
                        />
                    ) :
                      <Dropzone name={'fileName'} handleInputChange={handleInputChange}  />
                    }
                  </Box>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={currentTab} index={2}>
              <Grid container spacing={2}>
                <Grid xs={12}>
                  {resource.cover ? (
                    <Box p={1} my={2}>
                      <RemoveContent
                        files={[`${import.meta.env.VITE_AWS_URL}/resources/${resource.cover}`]}
                        onHandleClick={handleRemoveCover}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body2" color="textSecondary">
                        {`${t('cover-image')}:`}
                      </Typography>
                      <Dropzone name={'cover'} handleInputChange={handleInputChange} />
                    </>
                  )}
                </Grid>
              </Grid>
            </TabPanel>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={[!isValidForm && Object.keys(msgForm).length > 0 && { bgcolor: 'warning.light' }]}>
        <DeleteButton id="btn-delete-resource" onClick={() => dispatch(deleteResource(resource.id))}>
          {t('delete')}
        </DeleteButton>
        <CancelButton id="btn-cancel-resource" onClick={() => dispatch(toggleFormEditResources())}>
          {t('cancel')}
        </CancelButton>
        <SaveButton id="btn-save-resource" onClick={() => handleSubmit()}>
          {t('save')}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  )
}

export default ResourceModal
